import { ActionIcon, Button, Loader, Text, TextInput } from "@mantine/core";
import { usePrevious } from "@mantine/hooks";
import {
  IconCurrentLocation,
  IconCurrentLocationFilled,
  IconPencil,
} from "@tabler/icons-react";
import {
  AdvancedMarker,
  APIProvider,
  ControlPosition,
  Map,
  MapControl,
  Pin,
  useAdvancedMarkerRef,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { t } from "i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "~/hooks/use-typed-translation";

import ClientOnly from "../client-only";
import classes from "./google-map.module.scss";

// Google map url: https://www.google.com/maps/@${lat},${lng},13z

interface VisGoogleMapProps {
  setLatLng: (arg: string) => void;
}
export const VisGoogleMap = ({ setLatLng }: VisGoogleMapProps) => {
  const center = {
    lat: 5.628194,
    lng: -0.138192,
  };

  const { t } = useTranslation();
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [isDragging, setIsDragging] = useState(false);
  const isPrevDragging = usePrevious(isDragging);
  const [showConfirmLocation, setShowConfirmLocation] = useState(false);
  const [currentPosition, setCurrentPosition] =
    useState<google.maps.LatLng | null>(null);
  const [hasConfirmedLocation, setHasConfirmedLocation] = useState(false);
  const [currentAddress, setCurrentAddress] = useState("");
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState<google.maps.LatLng | null>(
    null
  );

  useEffect(() => {
    // console.log("isDragging, isPrevDragging:", isDragging, isPrevDragging);
    // console.log(
    //   "currentPos, prevCurrentPos:",
    //   currentPosition?.toString(),
    //   prevCurrentPosition?.toUrlValue()
    // );
    if ((!isDragging && isPrevDragging) || currentPosition) {
      setShowConfirmLocation(true);
      setHasConfirmedLocation(false);
    } else if (isDragging) {
      setShowConfirmLocation(false);
    }
    // else setShowConfirmLocation(false);
  }, [isPrevDragging, isDragging, currentPosition]);

  return (
    <>
      <ClientOnly>
        {hasConfirmedLocation ? null : (
          <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAP_API_KEY}>
            <div className={classes["map-container"]}>
              <Map
                mapId={import.meta.env.VITE_GOOGLE_MAP_ID}
                defaultZoom={9}
                defaultCenter={center}
                gestureHandling={"greedy"}
                disableDefaultUI={true}>
                <AdvancedMarker
                  position={currentPosition}
                  ref={markerRef}
                  draggable={true}
                  onDragStart={() => setIsDragging(true)}
                  onDragEnd={e => {
                    setIsDragging(false);
                    if (e.latLng) {
                      setCurrentPosition(e.latLng);
                    }
                  }}>
                  <Pin
                    background={"var(--mantine-color-primary-6)"}
                    borderColor={"var(--mantine-color-primary-6)"}
                    glyphColor={"var(--mantine-color-white)"}
                  />
                </AdvancedMarker>
              </Map>
              <MapControl position={ControlPosition.BLOCK_START_INLINE_CENTER}>
                <div className={classes["autocomplete-control"]}>
                  <PlaceAutocomplete
                    onPlaceSelect={setSelectedPlace}
                    value={currentAddress}
                    onChange={value => setCurrentAddress(value)}
                  />
                </div>
              </MapControl>

              <MapControl position={ControlPosition.RIGHT_BOTTOM}>
                <div className={classes["current-location-button"]}>
                  <ActionIcon
                    title={t("get-current-location")}
                    aria-label={t("get-current-location")}
                    bg="white"
                    loading={isLoadingLocation}
                    color="primary"
                    variant="light"
                    radius={"xl"}
                    onClick={() => {
                      // if (navigator.geolocation) {
                      //   setIsLoadingLocation(true);
                      // }
                      setIsLoadingLocation(true);
                    }}
                    size="lg"
                    disabled={isLoadingLocation}>
                    {userLocation?.equals(currentPosition) ? (
                      <IconCurrentLocationFilled size={25} />
                    ) : (
                      <IconCurrentLocation size={25} />
                    )}
                  </ActionIcon>
                </div>
              </MapControl>

              <LocationHandler
                marker={marker}
                setCurrentPosition={setCurrentPosition}
                isLoadingLocation={isLoadingLocation}
                setIsLoadingLocation={setIsLoadingLocation}
                setUserLocation={setUserLocation}
              />

              <MapHandler
                place={selectedPlace}
                marker={marker}
                setCurrentPosition={setCurrentPosition}
                setCurrentAddress={setCurrentAddress}
                setIsReverseGeocoding={setIsReverseGeocoding}
              />
            </div>
          </APIProvider>
        )}
        <>
          {/* Instruction tooltip */}
          {hasConfirmedLocation ? null : currentPosition ? (
            <div className={classes["instruction-tooltip"]}>
              {isDragging ? (
                <Text fz={14}>{t("release-to-set-location")}</Text>
              ) : (
                <Text fz={14}>{t("drag-pin")}</Text>
              )}
            </div>
          ) : null}

          {hasConfirmedLocation && (
            <div className={classes["current-address"]}>
              <TextInput
                w="min(100%, 350px)"
                label={t("selected-location")}
                value={currentAddress}
                mt={"-0.5rem"}
                readOnly
                rightSection={
                  isReverseGeocoding ? (
                    <Loader size="xs" />
                  ) : (
                    <ActionIcon
                      variant="outline"
                      onClick={() => {
                        setHasConfirmedLocation(false);
                        setShowConfirmLocation(true);
                        setCurrentPosition(currentPosition);
                      }}>
                      <IconPencil size={16} stroke={1.5} />
                    </ActionIcon>
                  )
                }
              />
            </div>
          )}

          {/* Confirm location button */}
          {showConfirmLocation && !hasConfirmedLocation ? (
            <Button
              mt="0rem"
              w="fit-content"
              onClick={() => {
                // Handle the confirmed location here
                console.log(
                  `Location confirmed: ${currentPosition?.lat()},${currentPosition?.lng()}`
                );
                setLatLng(
                  `${currentPosition?.lat()},${currentPosition?.lng()}`
                );
                setHasConfirmedLocation(true);
                setShowConfirmLocation(false);
              }}>
              {t("confirm-location")}
            </Button>
          ) : null}
        </>
      </ClientOnly>
    </>
  );
};

interface LocationHandlerProps {
  marker: google.maps.marker.AdvancedMarkerElement | null;
  setCurrentPosition: React.Dispatch<
    React.SetStateAction<google.maps.LatLng | null>
  >;
  setUserLocation: React.Dispatch<
    React.SetStateAction<google.maps.LatLng | null>
  >;
  isLoadingLocation: boolean;
  setIsLoadingLocation: React.Dispatch<React.SetStateAction<boolean>>;
}

const LocationHandler = ({
  marker,
  setCurrentPosition,
  isLoadingLocation,
  setIsLoadingLocation,
  setUserLocation,
}: LocationHandlerProps) => {
  const map = useMap();

  // Watch for isLoadingLocation changes to trigger location fetch
  useEffect(() => {
    if (isLoadingLocation) {
      getCurrentLocation();
    }
  }, [isLoadingLocation]);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        const location = new google.maps.LatLng(latitude, longitude);
        setUserLocation(location);

        setCurrentPosition(location);
        if (marker) {
          marker.position = location;
        }

        if (map) {
          map.panTo(location);
          map.setZoom(15); // Zoom closer when using current location
        }

        setIsLoadingLocation(false);
      },
      error => {
        console.error("Error getting current location:", error);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  }, [map, marker]);

  return null;
};

interface PlaceAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  value?: string;
  onChange?: (value: string) => void;
}

const PlaceAutocomplete = ({
  onPlaceSelect,
  value = "",
  onChange,
}: PlaceAutocompleteProps) => {
  const { t } = useTranslation();
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ["geometry", "name", "formatted_address"],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener("place_changed", () => {
      const place = placeAutocomplete.getPlace();
      onPlaceSelect(place);
      if (onChange && place.formatted_address) {
        onChange(place.formatted_address);
      }
    });
  }, [onPlaceSelect, onChange, placeAutocomplete]);

  return (
    <div className={classes["autocomplete-container"]}>
      <TextInput
        ref={inputRef}
        value={value}
        onChange={e => onChange && onChange(e.target.value)}
        placeholder={t("search-for-your-location")}
      />
    </div>
  );
};

interface MapHandlerProps {
  place: google.maps.places.PlaceResult | null;
  marker: google.maps.marker.AdvancedMarkerElement | null;
  setCurrentPosition: React.Dispatch<
    React.SetStateAction<google.maps.LatLng | null>
  >;
  setCurrentAddress: React.Dispatch<React.SetStateAction<string>>;
  setIsReverseGeocoding: React.Dispatch<React.SetStateAction<boolean>>;
}

const MapHandler = ({
  place,
  marker,
  setCurrentPosition,
  setCurrentAddress,
  setIsReverseGeocoding,
}: MapHandlerProps) => {
  const map = useMap();
  const geocodingLibrary = useMapsLibrary("geocoding");
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);

  // Initialize geocoder
  useEffect(() => {
    if (!geocodingLibrary) return;
    setGeocoder(new geocodingLibrary.Geocoder());
  }, [geocodingLibrary]);

  // Handle place selection from autocomplete
  useEffect(() => {
    if (!map || !place || !marker) return;

    if (place.geometry?.viewport) {
      map.fitBounds(place.geometry?.viewport);
    }

    const newPosition = place.geometry?.location;
    if (newPosition) {
      marker.position = newPosition;
      setCurrentPosition(newPosition);
    }

    if (place.formatted_address) {
      setCurrentAddress(place.formatted_address);
    }
  }, [map, place, marker, setCurrentPosition, setCurrentAddress]);

  // Reverse geocode when marker position changes
  useEffect(() => {
    if (!geocoder || !marker || !marker.position) return;

    // Debounce the reverse geocoding to avoid too many API calls
    const timeoutId = setTimeout(() => {
      setIsReverseGeocoding(true);

      geocoder.geocode(
        { location: marker.position as google.maps.LatLng },
        (results, status) => {
          setIsReverseGeocoding(false);

          if (status === "OK" && results && results[0]) {
            setCurrentAddress(results[0].formatted_address);
          } else {
            setCurrentAddress(
              // @ts-expect-error Still works but I get TS errors for some reason
              `Location: ${marker.position?.lat().toFixed(6)}, ${marker.position
                // @ts-expect-error Still works but I get TS errors for some reason
                ?.lng()
                .toFixed(6)}`
            );
          }
        }
      );
    }, 500); // Wait 500ms after position change before geocoding

    return () => clearTimeout(timeoutId);
  }, [marker?.position, geocoder, setCurrentAddress, setIsReverseGeocoding]);

  // Add click handler to the map to allow setting the marker by clicking
  useEffect(() => {
    if (!map || !marker) return;

    const clickListener = map.addListener(
      "click",
      (e: google.maps.MapMouseEvent) => {
        marker.position = e.latLng;
        setCurrentPosition(e.latLng);
      }
    );

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [map, marker, setCurrentPosition]);

  return null;
};
